"use client"

import { useMemo, useState } from "react"
import { ArrowUpRight, Info, RefreshCw } from "lucide-react"
import {
  StatBorderGrid,
  ToolPageShell,
  ToolProductCarousel,
  ToolShareBar,
} from "@/components/brand-kit"
import { TOOL_METADATA } from "@/lib/tool-metadata"
import { readNonNegative, readQuantity } from '@/lib/tools/numeric-input'
import { REFRIGERATOR_LIFESPAN } from '@/lib/refrigerator-answer-facts'
import { ENERGY_STAR_PLANNING_LIFETIME_YEARS } from '@/lib/tools/refrigerator-lifespan'

// Sources:
//   • DOE federal refrigerator energy-conservation standards (10 CFR 430):
//     successive standard rounds in 1990, 2001, and 2014. Pre-2014 units run
//     materially higher kWh than current-spec models.
//   • ENERGY STAR refrigerator key product criteria: certified models exceed
//     the federal floor by ≥ 10%.
//   • EIA average US residential electricity rate: ~16.5¢/kWh.
//   • Projection horizon: 10 years, shorter than the 12-year refrigerator
//     lifetime ENERGY STAR's savings comparison assumes, so the payback
//     estimate stays conservative (FRIDGE-1439; lib/tools/refrigerator-lifespan.ts).

const FALLBACK_RELATED = [
  "energy-cost-calculator",
  "refrigerator-rebate-finder",
  "refrigerator-energy-efficiency-calculator",
  "refrigerator-cost-of-ownership-calculator",
]

function buildRelated(slugs: string[], currentSlug: string) {
  const seen = new Set<string>([currentSlug])
  const out: { title: string; category: string; description: string; href: string; time: string }[] = []
  const candidates = [...slugs, ...FALLBACK_RELATED]
  for (const s of candidates) {
    if (out.length === 4) break
    if (seen.has(s)) continue
    const m = TOOL_METADATA[s]
    if (!m) continue
    seen.add(s)
    out.push({
      title: m.name,
      category: m.category,
      description: m.shortDescription,
      href: `/tools/${s}`,
      time: "~30 sec",
    })
  }
  return out
}

const META: { name: string; category: string; relatedTools: string[] } = {
  name: "Refrigerator Upgrade Savings Calculator",
  category: "Energy & Cost",
  relatedTools: [
    "energy-cost-calculator",
    "refrigerator-rebate-finder",
    "refrigerator-energy-efficiency-calculator",
    "refrigerator-cost-of-ownership-calculator",
  ],
}

export function EnergySavingsRoiCalculatorPageClient() {
  const meta = META

  const [newFridgeCost, setNewFridgeCost] = useState("1500")
  const [oldKwhYear, setOldKwhYear] = useState("900")
  const [newKwhYear, setNewKwhYear] = useState("500")
  const [electricityRate, setElectricityRate] = useState("16.5")
  const [rebateAmount, setRebateAmount] = useState("75")

  const result = useMemo(() => {
    const cost = readQuantity(newFridgeCost, { min: 1, max: 20000 })
    const oldKwh = readQuantity(oldKwhYear, { min: 50, max: 5000 })
    const newKwh = readQuantity(newKwhYear, { min: 50, max: 3000 })
    const rate = readQuantity(electricityRate, { min: 1, max: 100 })
    const rebate = readNonNegative(rebateAmount, { max: 5000 }) ?? 0

    if (cost === null || !oldKwh || !newKwh || !rate) return null

    const kwhSaved = Math.max(0, oldKwh - newKwh)
    const annualSavings = (kwhSaved * rate) / 100
    const monthlySavings = annualSavings / 12
    const lifetimeSavings = annualSavings * 10
    const netCost = Math.max(0, cost - rebate)
    const paybackYears = annualSavings > 0 ? netCost / annualSavings : Infinity
    const roi10Year = netCost > 0 ? ((annualSavings * 10 - netCost) / netCost) * 100 : 0

    let recommendation = ""
    if (!isFinite(paybackYears)) {
      recommendation = "The new model isn't more efficient than the old one — energy alone won't pay back the upgrade."
    } else if (paybackYears < 3) {
      recommendation = "Excellent investment — energy alone pays it back in under 3 years."
    } else if (paybackYears < 5) {
      recommendation = "Good investment — payback inside the 10-year horizon used here."
    } else if (paybackYears < 8) {
      recommendation = "Fair investment — energy savings recover most of the cost over the 10-year horizon."
    } else {
      recommendation = "Long payback — only worth it if your current fridge is failing or you value other features."
    }

    return {
      annualSavings,
      monthlySavings,
      lifetimeSavings,
      paybackYears,
      netCost,
      roi10Year,
      recommendation,
    }
  }, [newFridgeCost, oldKwhYear, newKwhYear, electricityRate, rebateAmount])

  return (
    <ToolPageShell
      toolName={meta.name}
      heroEyebrow={meta.category}
      heroTitlePre="Will the new fridge"
      heroTitleItalic="actually"
      heroTitlePost="pay for itself?"
      heroSubtitle="Enter the purchase price, both kWh figures, your electricity rate, and any rebate. We'll show the payback period and 10-year ROI on real numbers — not marketing math."
      heroMeta={["⏱ ~45 sec", "DOE + ENERGY STAR + EIA data"]}
      heroShare={<ToolShareBar title={meta.name} />}
      heroRight={
        <StatBorderGrid
          stats={[
            { value: "10%+", label: "ENERGY STAR vs federal min", note: "ENERGY STAR criteria" },
            { value: "10 yr", label: "Service-life baseline", note: "Industry typical" },
            { value: "16.5¢", label: "US avg rate", note: "EIA" },
            { value: "Up to $100s", label: "Utility rebates available", note: "Varies by ZIP" },
          ]}
          className="w-full max-w-md md:max-w-none md:w-auto mx-auto md:mx-0"
        />
      }
      faqs={[
        {
          question: "Where do the kWh numbers come from?",
          answer:
            "Both come off the yellow EnergyGuide label that ships on every new fridge, or the spec sheet on the manufacturer's website. The figure you want is 'Estimated Yearly Electricity Use' or 'kWh/year'.",
        },
        {
          question: "What if my old fridge doesn't have an EnergyGuide label anymore?",
          answer:
            "Look up the model number in the manufacturer's service archives or the ENERGY STAR retired-product database. As a rough planning estimate, pre-1990 units often draw 1,000+ kWh/yr; 1990–2001 era units 700–900; 2001–2014 era units 500–700; post-2014 ENERGY STAR units 400–600.",
        },
        {
          question: "Why does the payback use 10 years?",
          // FRIDGE-1439: the site's one lifespan figure (lib/refrigerator-answer-facts.ts),
          // not a typed range; the 10 years is this tool's horizon, not a lifespan.
          answer:
            `${REFRIGERATOR_LIFESPAN.shortAnswer} This calculator projects savings over 10 years instead, so the payback estimate stays conservative. A unit that keeps running longer returns more than shown.`,
        },
        {
          question: "Why include the rebate field?",
          answer:
            "Many utilities offer ENERGY STAR refrigerator rebates ranging from $25 to several hundred dollars depending on the program. The rebate reduces your effective net cost, which directly shortens payback period. Use our rebate finder to see what's available in your ZIP.",
        },
        {
          question: "Does this account for repair savings on the old unit?",
          answer:
            "No — the calculator isolates the energy-only ROI on purpose. If you're avoiding a known impending repair, factor that into the decision separately or use our repair-vs-replace tool.",
        },
      ]}
      relatedTools={buildRelated(meta.relatedTools, "refrigerator-upgrade-savings-calculator")}
      cta={{
        title: "Ready to shop the upgrade?",
        body: "Browse ENERGY STAR–certified refrigerators with the lowest annual kWh — sortable by capacity, type, and live retailer pricing.",
        buttonLabel: "Browse ENERGY STAR refrigerators",
        buttonHref: "/collections/energy-star-appliances",
      }}
    >
      {/* ============ CALCULATOR ============ */}
      <section className="bg-gray-50 py-8 md:py-12">
        <div className="max-w-[1120px] mx-auto px-6 md:px-8 space-y-6">
          <div className="grid lg:grid-cols-2 gap-6 lg:items-stretch">
            {/* Input card */}
            <div className="bg-white rounded-xl p-6 md:p-7 border border-gray-900 flex flex-col">
              <div className="flex items-center gap-3 mb-2">
                <span className="font-mono text-[12px] font-bold tracking-[0.15em] text-[#ff9951]">
                  STEP 01
                </span>
                <div className="flex-1 h-[1px] bg-gray-300" />
              </div>
              <h2
                className="text-gray-900 font-black"
                style={{ fontSize: 28, letterSpacing: "-0.025em", lineHeight: 1.05 }}
              >
                Five inputs.
                <br />
                Honest payback.
              </h2>
              <p className="text-[14px] text-gray-600 mt-2 leading-[1.5]">
                All numbers come from your EnergyGuide labels and your most recent electric bill.
              </p>

              <div className="mt-7 space-y-4">
                <div>
                  <label className="block text-[11px] font-bold uppercase tracking-[0.12em] text-gray-900 mb-2">
                    New fridge purchase price
                  </label>
                  <div className="relative">
                    <input
                      type="number"
                      value={newFridgeCost}
                      onChange={(e) => setNewFridgeCost(e.target.value)}
                      className="tool-input-clean w-full px-4 py-4 text-[20px] font-bold rounded-lg outline-none transition-colors border border-gray-300 focus:border-[#ff9951]"
                      style={{ fontVariantNumeric: "tabular-nums" }}
                    />
                    <span className="absolute right-4 top-1/2 -translate-y-1/2 text-[13px] font-mono text-gray-500">
                      $
                    </span>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-[11px] font-bold uppercase tracking-[0.12em] text-gray-900 mb-2">
                      Old kWh/yr
                    </label>
                    <input
                      type="number"
                      value={oldKwhYear}
                      onChange={(e) => setOldKwhYear(e.target.value)}
                      className="tool-input-clean w-full px-4 py-4 text-[18px] font-bold rounded-lg outline-none transition-colors border border-gray-300 focus:border-[#ff9951]"
                      style={{ fontVariantNumeric: "tabular-nums" }}
                    />
                  </div>
                  <div>
                    <label className="block text-[11px] font-bold uppercase tracking-[0.12em] text-gray-900 mb-2">
                      New kWh/yr
                    </label>
                    <input
                      type="number"
                      value={newKwhYear}
                      onChange={(e) => setNewKwhYear(e.target.value)}
                      className="tool-input-clean w-full px-4 py-4 text-[18px] font-bold rounded-lg outline-none transition-colors border border-gray-300 focus:border-[#ff9951]"
                      style={{ fontVariantNumeric: "tabular-nums" }}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-[11px] font-bold uppercase tracking-[0.12em] text-gray-900 mb-2">
                      Rate (¢/kWh)
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      value={electricityRate}
                      onChange={(e) => setElectricityRate(e.target.value)}
                      className="tool-input-clean w-full px-4 py-4 text-[18px] font-bold rounded-lg outline-none transition-colors border border-gray-300 focus:border-[#ff9951]"
                      style={{ fontVariantNumeric: "tabular-nums" }}
                    />
                  </div>
                  <div>
                    <label className="block text-[11px] font-bold uppercase tracking-[0.12em] text-gray-900 mb-2">
                      Rebate ($)
                    </label>
                    <input
                      type="number"
                      value={rebateAmount}
                      onChange={(e) => setRebateAmount(e.target.value)}
                      className="tool-input-clean w-full px-4 py-4 text-[18px] font-bold rounded-lg outline-none transition-colors border border-gray-300 focus:border-[#ff9951]"
                      style={{ fontVariantNumeric: "tabular-nums" }}
                    />
                  </div>
                </div>

                <div className="pt-2 text-[11px] uppercase tracking-[0.12em] text-gray-500 font-bold flex items-center gap-1.5">
                  <RefreshCw className="w-3 h-3" /> Updates live as you type
                  <Info className="w-3.5 h-3.5 ml-2" /> Use your bill&apos;s total dollars ÷ total kWh for rate
                </div>
              </div>
            </div>

            {/* Result panel */}
            <div className="flex flex-col">
              <div
                className="rounded-2xl p-6 md:p-8 relative overflow-hidden text-white flex-1"
                style={{ background: "linear-gradient(135deg, #0469f0 0%, #0053c4 100%)" }}
              >
                <div
                  aria-hidden
                  className="absolute top-0 right-0 w-[340px] h-[340px] rounded-full pointer-events-none"
                  style={{
                    background: "rgba(255,153,81,0.35)",
                    filter: "blur(60px)",
                    transform: "translate(25%, -35%)",
                  }}
                />
                <div className="relative">
                  <div className="flex items-center justify-between mb-8 flex-wrap gap-2">
                    <div className="text-[11px] font-bold uppercase tracking-[0.15em] text-white/70">
                      Payback period
                    </div>
                    <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-[0.15em] text-white/60">
                      <RefreshCw className="w-3 h-3" /> Updated live
                    </div>
                  </div>
                  <div
                    className="font-black"
                    style={{
                      fontSize: "clamp(36px, 8vw, 88px)",
                      letterSpacing: "-0.045em",
                      lineHeight: 0.9,
                    }}
                  >
                    {result && isFinite(result.paybackYears)
                      ? `${result.paybackYears.toFixed(1)}`
                      : "—"}
                    <span className="text-[20px] md:text-[24px] font-black tracking-tighter text-white/60 ml-2">
                      years
                    </span>
                  </div>
                  <div className="mt-4 text-[14px] text-white/80 leading-[1.5]">
                    {result?.recommendation ?? "Enter the five inputs to see payback."}
                  </div>
                  <div className="grid grid-cols-2 gap-2 sm:gap-3 mt-6 md:mt-8">
                    <div className="rounded-lg p-3 sm:p-4 bg-white/10 border border-white/15">
                      <div className="text-[10px] uppercase tracking-[0.15em] text-white/60 font-bold mb-1">
                        Annual savings
                      </div>
                      <div className="text-[18px] sm:text-[22px] font-black tracking-tight">
                        {`$${result?.annualSavings.toLocaleString("en-US", { maximumFractionDigits: 0 }) ?? "—"}`}
                      </div>
                    </div>
                    <div className="rounded-lg p-3 sm:p-4 bg-white/10 border border-white/15">
                      <div className="text-[10px] uppercase tracking-[0.15em] text-white/60 font-bold mb-1">
                        10-yr lifetime
                      </div>
                      <div className="text-[18px] sm:text-[22px] font-black tracking-tight">
                        {`$${result?.lifetimeSavings.toLocaleString("en-US", { maximumFractionDigits: 0 }) ?? "—"}`}
                      </div>
                    </div>
                    <div className="rounded-lg p-3 sm:p-4 bg-white/10 border border-white/15">
                      <div className="text-[10px] uppercase tracking-[0.15em] text-white/60 font-bold mb-1">
                        Net cost after rebate
                      </div>
                      <div className="text-[18px] sm:text-[22px] font-black tracking-tight">
                        {`$${result?.netCost.toLocaleString("en-US", { maximumFractionDigits: 0 }) ?? "—"}`}
                      </div>
                    </div>
                    <div className="rounded-lg p-3 sm:p-4 bg-white/10 border border-white/15">
                      <div className="text-[10px] uppercase tracking-[0.15em] text-white/60 font-bold mb-1">
                        10-yr ROI
                      </div>
                      <div className="text-[18px] sm:text-[22px] font-black tracking-tight">
                        {result && isFinite(result.roi10Year)
                          ? `${result.roi10Year.toLocaleString("en-US", { maximumFractionDigits: 0 })}%`
                          : "—"}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============ FIRST CAROUSEL ============ */}
      <section className="bg-white py-4 md:py-6">
        <ToolProductCarousel
          eyebrow="Step 02 · Shop the upgrade"
          title="Counter-depth fridges to compare"
          description="Models that exceed federal efficiency standards by 10% or more."
          collectionType="counter-depth"
          fallbackCollectionType="counter-depth"
        />
      </section>

      {/* ============ METHODOLOGY ============ */}
      <section className="bg-gray-50 py-8 md:py-12 border-y border-gray-200">
        <div className="max-w-[1120px] mx-auto px-6 md:px-8">
          <div className="grid md:grid-cols-[1fr_1.3fr] gap-8 md:gap-12 items-start">
            <div>
              <div className="text-[11px] font-bold uppercase tracking-[0.15em] mb-3 text-[#ff9951]">
                Under the hood
              </div>
              <h2
                className="text-gray-900 font-black"
                style={{ fontSize: "clamp(24px, 3vw, 36px)", letterSpacing: "-0.03em", lineHeight: 1.05 }}
              >
                kWh × rate. Net cost ÷ <em className="italic text-[#ff9951]">savings.</em>
              </h2>
              <p className="text-[14px] text-gray-600 mt-4 max-w-[400px] leading-[1.55]">
                Reference numbers from DOE federal standards, the ENERGY STAR certified product
                database and EIA average rates, projected over a conservative 10-year horizon.
              </p>
            </div>
            <ol className="list-none p-0 m-0 border-t border-gray-900">
              {[
                {
                  n: "01",
                  t: "kWh saved × rate = annual $",
                  d: "(old kWh − new kWh) × ¢/kWh ÷ 100 = honest annual savings, in your dollars at your rate.",
                },
                {
                  n: "02",
                  t: "Net cost = price − rebate",
                  d: "Utility ENERGY STAR rebates can run from $25 to several hundred dollars depending on program. The rebate directly shortens payback period.",
                },
                {
                  n: "03",
                  t: "Payback + 10-year ROI",
                  d: `Net cost ÷ annual savings = years to break even. Lifetime savings and ROI % are projected over 10 years. That is shorter than the ${ENERGY_STAR_PLANNING_LIFETIME_YEARS}-year refrigerator lifetime ENERGY STAR assumes, which keeps the estimate conservative.`,
                },
              ].map((s) => (
                <li
                  key={s.n}
                  className="grid grid-cols-[auto_1fr_auto] gap-4 sm:gap-7 items-center py-5 sm:py-6 border-b border-gray-900"
                >
                  <div className="font-mono text-sm font-bold tracking-[0.05em] min-w-[28px] sm:min-w-[34px] text-[#ff9951]">
                    {s.n}
                  </div>
                  <div>
                    <h3
                      className="text-gray-900"
                      style={{ fontSize: 22, fontWeight: 800, letterSpacing: "-0.02em" }}
                    >
                      {s.t}
                    </h3>
                    <p className="mt-1.5 text-[14px] text-gray-600 leading-[1.55]">{s.d}</p>
                  </div>
                  <ArrowUpRight className="w-4 h-4 sm:w-5 sm:h-5 text-gray-500 flex-shrink-0" />
                </li>
              ))}
            </ol>
          </div>
        </div>
      </section>

      {/* ============ SECOND CAROUSEL ============ */}
      <section className="bg-white py-4 md:py-6">
        <ToolProductCarousel
          eyebrow="You might also like"
          title="Counter-depth refrigerators"
          description="A built-in look at full-size capacity."
          collectionType="counter-depth"
          fallbackCollectionType="french-door"
        />
      </section>
    </ToolPageShell>
  )
}
